// The weather_forecast step Result (API-3689) as a card (WEB-7117), and weather_observation
// (API-3888) shaped like it. Times show in the place's own local time, from TimeZoneOffset, whatever
// the browser's time zone is.

const num = v => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const round = v => (num(v) == null ? null : Math.round(v))
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const HOURS_SHOWN = 24
const STEP_HOURS = 3

// "-06:00" as minutes east of UTC (-360), or null.
export const offsetMinutes = offset => {
  const m = /^([+-])(\d{2}):?(\d{2})$/.exec(String(offset || '').trim())
  if (!m) return null
  return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]))
}

// "4 PM" for an ISO time, in the place's time (TimeZoneOffset), falling back to the time's own offset.
export const localHourLabel = (iso, zoneOffset) => {
  const t = Date.parse(iso || '')
  if (!Number.isFinite(t)) return null
  let offset = offsetMinutes(zoneOffset)
  if (offset == null) {
    const own = /([+-]\d{2}:?\d{2}|Z)$/.exec(String(iso))
    offset = own ? (own[1] === 'Z' ? 0 : offsetMinutes(own[1])) : 0
  }
  const hour = new Date(t + offset * 60000).getUTCHours()
  if (hour === 0) return '12 AM'
  if (hour === 12) return 'Noon'
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`
}

// "Tue" for "2026-09-29"; "Today" for the first day.
const dayName = (ymd, i) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '')
  if (!m) return i === 0 ? 'Today' : ''
  return i === 0 ? 'Today' : DAYS[new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay()]
}

// "6:58 AM" from an ISO time or an "HH:MM" clock; as given otherwise.
const clockTime = (value, zoneOffset) => {
  if (value == null || value === '') return null
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const t = Date.parse(s)
    if (!Number.isFinite(t)) return s
    let offset = offsetMinutes(zoneOffset)
    if (offset == null) { const own = /([+-]\d{2}:?\d{2})$/.exec(s); offset = own ? offsetMinutes(own[1]) : 0 }
    const d = new Date(t + offset * 60000)
    return twelveHour(d.getUTCHours(), d.getUTCMinutes())
  }
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(s.trim())
  return m ? twelveHour(Number(m[1]), Number(m[2])) : s
}

const twelveHour = (h, min) => `${h % 12 === 0 ? 12 : h % 12}:${String(min).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`

const sentence = s => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s)

// { title, days: [{ key, name, weather, high, low, precip, wind }], sun, moon, hours: [{ key, label, arrowDeg, speed, gust, temp, isDay }], notes }
export const weatherCard = result => {
  if (!result || result.Kind !== 'weather_forecast') return null
  const days = Array.isArray(result.Days) ? result.Days : []
  const hourly = Array.isArray(result.Hourly) ? result.Hourly : []
  if (days.length === 0 && hourly.length === 0) return null
  const zone = result.TimeZoneOffset
  // Winds are given as where they come from; the arrow shows where they go.
  const blowsToward = deg => (num(deg) == null ? null : (deg + 180) % 360)
  const first = days[0] || {}
  const sun = [clockTime(first.Sunrise, zone) && `Sunrise ${clockTime(first.Sunrise, zone)}`, clockTime(first.Sunset, zone) && `Sunset ${clockTime(first.Sunset, zone)}`].filter(Boolean).join(' · ') || null
  const moonTimes = [clockTime(first.Moonrise, zone) && `Moonrise ${clockTime(first.Moonrise, zone)}`, clockTime(first.Moonset, zone) && `Moonset ${clockTime(first.Moonset, zone)}`].filter(Boolean)
  const phase = first.MoonPhase ? `${sentence(first.MoonPhase)}${num(first.MoonIllumination) != null ? ` ${Math.round(first.MoonIllumination)}%` : ''}` : null
  const moon = [phase, ...moonTimes].filter(Boolean).join(' · ') || null

  return {
    title: `Forecast · ${days.length > 1 ? `${days.length} days` : days.length === 1 ? 'today' : 'next 48 hours'}`,
    days: days.slice(0, 3).map((d, i) => ({
      key: d.Date || String(i),
      // In the evening today's high has passed and the forecast leaves it out: that's tonight.
      name: i === 0 && num(d.HighF) == null && num(d.LowF) != null ? 'Tonight' : dayName(d.Date, i),
      weather: d.Weather || null,
      high: round(d.HighF),
      low: round(d.LowF),
      precip: round(d.PrecipPercent),
      wind: [d.WindDir, num(d.WindMph) != null ? `${round(d.WindMph)} mph` : null].filter(Boolean).join(' ') || null
    })),
    sun,
    moon,
    hours: hourly.slice(0, HOURS_SHOWN).filter((_, i) => i % STEP_HOURS === 0).map((h, i) => ({
      key: h.Time || String(i),
      label: localHourLabel(h.Time, zone) || '',
      direction: h.Direction || null,
      arrowDeg: blowsToward(h.DirectionDeg),
      speed: round(h.SpeedMph),
      gust: round(h.GustMph),
      temp: round(h.TempF),
      isDay: h.IsDay !== false
    })),
    notes: Array.isArray(result.Notes) ? result.Notes.filter(n => typeof n === 'string' && n) : []
  }
}

// "Oct 28 · 4:00 PM" from an observation's Time, which is already the place's local time with its
// offset ("2026-10-28T16:00-06:00"), so the clock is read off the string rather than converted.
export const localTimeLabel = iso => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(iso || ''))
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])} · ${twelveHour(Number(m[4]), Number(m[5]))}` : null
}

const inches = (v, digits) => (num(v) == null ? null : `${Number(v).toFixed(digits)} in`)

// get_weather_observation: observed weather now (Current) or at past hours. An hour with no data has
// Found false and no figures.
//   { title, rows: [{ key, when, current, found, weather, temp, feelsLike, high, low, wind, gust, pressure, precip, humidity }], notes }
export const weatherObservationCard = result => {
  if (!result || result.Kind !== 'weather_observation') return null
  const observations = Array.isArray(result.Observations) ? result.Observations : []
  if (observations.length === 0) return null
  const current = observations.some(o => o && o.Current === true)
  return {
    title: current && observations.length === 1 ? 'Weather now' : 'Observed weather',
    rows: observations.slice(0, 6).map((o, i) => {
      const when = localTimeLabel(o.Time)
      return {
        key: o.Time || String(i),
        when: o.Current ? ['Now', when].filter(Boolean).join(' · ') : when || 'Time unknown',
        current: o.Current === true,
        found: o.Found !== false,
        weather: o.Weather || null,
        temp: round(o.TempF),
        feelsLike: round(o.FeelsLikeF),
        high: round(o.HighF24h),
        low: round(o.LowF24h),
        wind: [o.WindDir, num(o.WindMph) != null ? `${round(o.WindMph)} mph` : null].filter(Boolean).join(' ') || null,
        gust: round(o.GustMph),
        pressure: num(o.PressureIn) != null ? `${Number(o.PressureIn).toFixed(2)} inHg` : null,
        precip: inches(o.PrecipIn24h, 2),
        humidity: round(o.HumidityPct)
      }
    }),
    notes: Array.isArray(result.Notes) ? result.Notes.filter(n => typeof n === 'string' && n) : []
  }
}
